/**
 * /market/:slug/categoria/:categoria — Landing comercial de categoría (Ola 1.2).
 *
 * Igual patrón que la PDP (`market_.$slug.p.$productSlug.tsx`): la ruta solo
 * resuelve catálogo + categoría y arma el `head()` con SEO propio; toda la UI
 * vive en `MarketLandingPage` (reutiliza chrome + MarketCatalogSection).
 */
import { createFileRoute, redirect } from "@tanstack/react-router";
import { createServerFn } from "@tanstack/react-start";
import { getRequest } from "@tanstack/react-start/server";
import { FolderX } from "lucide-react";
import { loadMarketCatalog, type MarketCatalogResult } from "@/lib/market-catalog";
import { renderMarketPublicOffline } from "@/lib/market-public-offline-gate";
import { MarketLandingPage } from "@/components/market/MarketLandingPage";
import { MarketPlpSkeleton } from "@/components/market/MarketSkeletons";
import {
  findCategoryByParam,
  getPublishedPlp,
  landingPath,
  landingSeoHead,
  resolveLandingConfig,
} from "@/lib/market-landing";

const fetchCatalog = createServerFn({ method: "GET" })
  .inputValidator((slug: string) => slug)
  // @ts-expect-error ServerFnCtx framework type mismatch
  .handler(async ({ data: slug }): Promise<MarketCatalogResult> => {
    return loadMarketCatalog(slug, { request: getRequest() });
  });

const FONT_LINK = {
  rel: "stylesheet",
  href: "https://fonts.googleapis.com/css2?family=Hanken+Grotesk:wght@400;600;700;800&family=Inter:wght@400;500;600&family=JetBrains+Mono:wght@600&display=swap",
};

export const Route = createFileRoute("/market_/$slug/categoria/$categoria")({
  pendingComponent: MarketPlpSkeleton,
  pendingMs: 0,
  loader: async ({ params }) => {
    const result = await fetchCatalog({ data: params.slug }) as MarketCatalogResult;
    if (result.ok) {
      const category = findCategoryByParam(result.data.categories, params.categoria);
      if (!category) {
        throw redirect({ to: "/market/$slug/catalogo", params: { slug: params.slug }, search: {} });
      }
    }
    return result;
  },
  component: CategoryLandingRoute,
  head: ({ params, loaderData }) => {
    const manifestLink = { rel: "manifest", href: `/api/manifest/${params.slug}` };
    const result = loaderData as MarketCatalogResult | undefined;
    if (!result?.ok) {
      return { meta: [{ title: `${params.categoria} — ${params.slug}` }], links: [manifestLink, FONT_LINK] };
    }

    const data = result.data;
    const category = findCategoryByParam(data.categories, params.categoria);
    if (!category) {
      return {
        meta: [{ title: `Categoría — ${data.tenant.name}` }],
        links: [manifestLink, FONT_LINK],
      };
    }

    const plp = getPublishedPlp(data);
    const landing = plp ? resolveLandingConfig(plp, "category", category.id) : null;
    const seo = landing
      ? landingSeoHead({
          storeName: data.tenant.name,
          landing,
          fallbackTitle: category.name,
          canonicalPath: landingPath(params.slug, "category", category.slug),
        })
      : null;

    return {
      meta: seo?.meta ?? [
        { title: `${category.name} — ${data.tenant.name}` },
        { name: "description", content: `Explorá ${category.name} en ${data.tenant.name}` },
      ],
      links: [...(seo?.links ?? []), manifestLink, FONT_LINK],
      scripts: seo?.jsonLd
        ? [
            {
              tag: "script" as const,
              attrs: { type: "application/ld+json" },
              children: JSON.stringify(seo.jsonLd),
            },
          ]
        : undefined,
    };
  },
});

function CategoryLandingRoute() {
  const { slug, categoria } = Route.useParams();
  const result = Route.useLoaderData() as MarketCatalogResult;

  const offlineScreen = renderMarketPublicOffline(result);
  if (offlineScreen) return offlineScreen;

  if (!result.ok) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-3 p-6 text-center">
        <FolderX className="h-12 w-12 text-muted-foreground" />
        <h1 className="text-xl font-semibold">Tienda no disponible</h1>
        <p className="text-muted-foreground">{result.error}</p>
      </div>
    );
  }

  const data = result.data;
  const category = findCategoryByParam(data.categories, categoria);

  if (!category) {
    // Presets/bookmarks con slugs ficticios → PLP (no 404 muerto).
    throw redirect({ to: "/market/$slug/catalogo", params: { slug }, search: {} });
  }

  return (
    <MarketLandingPage
      slug={slug}
      data={data}
      kind="category"
      param={category.slug}
      categoryId={category.id}
      title={category.name}
    />
  );
}
